// Open Food Facts - free, no key needed, 2M+ products
import axios from 'axios';

export const searchFoodByBarcode = async (barcode) => {
  try {
    const res = await axios.get(`https://world.openfoodfacts.org/api/v0/product/${barcode}.json`);
    if (res.data.status === 1) {
      const p = res.data.product;
      return {
        name: p.product_name || 'Unknown Product',
        brand: p.brands || '',
        calories: p.nutriments?.['energy-kcal_100g'] || 0,
        protein: p.nutriments?.proteins_100g || 0,
        carbs: p.nutriments?.carbohydrates_100g || 0,
        fat: p.nutriments?.fat_100g || 0,
        fiber: p.nutriments?.fiber_100g || 0,
        sugar: p.nutriments?.sugars_100g || 0,
        sodium: p.nutriments?.sodium_100g || 0,
        servingSize: p.serving_size || '100g',
        image: p.image_url || null,
        barcode,
      };
    }
    return null;
  } catch {
    return null;
  }
};

const parseUSDA = (foods) =>
  (foods || []).filter(f => f.description).map(f => {
    const n = {};
    (f.foodNutrients || []).forEach(({ nutrientName: nm, value: v }) => {
      if (!nm) return;
      if (nm.includes('Energy') && !n.calories) n.calories = v || 0;
      if (nm === 'Protein') n.protein = v || 0;
      if (nm.includes('Carbohydrate, by difference')) n.carbs = v || 0;
      if (nm.includes('Total lipid')) n.fat = v || 0;
    });
    const scale = (f.servingSize && (f.servingSizeUnit || '').toLowerCase() === 'g')
      ? f.servingSize / 100 : 1;
    return {
      name: f.description,
      brand: f.brandOwner || f.brandName || '',
      calories: Math.round((n.calories || 0) * scale),
      protein: +((n.protein || 0) * scale).toFixed(1),
      carbs:   +((n.carbs   || 0) * scale).toFixed(1),
      fat:     +((n.fat     || 0) * scale).toFixed(1),
      servingSize: f.servingSize ? `${f.servingSize}${f.servingSizeUnit || 'g'}` : '100g',
      image: null,
      barcode: null,
    };
  });

const parseOFF = (products) =>
  (products || []).filter(p => p.product_name).map(p => {
    const n = p.nutriments || {};
    return {
      name: p.product_name,
      brand: p.brands || '',
      calories: Math.round(n['energy-kcal_serving'] || n['energy-kcal_100g'] || 0),
      protein: +((n['proteins_serving']      || n['proteins_100g']      || 0)).toFixed(1),
      carbs:   +((n['carbohydrates_serving'] || n['carbohydrates_100g'] || 0)).toFixed(1),
      fat:     +((n['fat_serving']           || n['fat_100g']           || 0)).toFixed(1),
      servingSize: p.serving_size || '100g',
      image: p.image_small_url || null,
      barcode: p.code,
    };
  });

const dedupByName = (items) => {
  const seen = new Set();
  return items.filter(item => {
    const key = item.name.toLowerCase().trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const searchFoodByName = async (query) => {
  const encoded = encodeURIComponent(query);
  const offBase = `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encoded}&search_simple=1&action=process&json=1&page_size=20`;

  const fetchUSDA = async () => {
    try {
      const res = await axios.get(
        `https://api.nal.usda.gov/fdc/v1/foods/search?query=${encoded}&dataType=Branded,Survey%20(FNDDS)&pageSize=10&api_key=DEMO_KEY`
      );
      return parseUSDA(res.data.foods);
    } catch { return []; }
  };

  const fetchOFF = async (usFilter) => {
    const extra = usFilter ? '&countries_tags=united-states&language_codes=en' : '';
    try {
      const res = await axios.get(`${offBase}${extra}`);
      return parseOFF(res.data.products);
    } catch { return []; }
  };

  const [usdaResults, offUSResults] = await Promise.all([fetchUSDA(), fetchOFF(true)]);

  let offResults = offUSResults;
  if (offUSResults.length < 3) {
    const fallback = await fetchOFF(false);
    const seen = new Set(offUSResults.map(r => r.name.toLowerCase()));
    offResults = [...offUSResults, ...fallback.filter(r => !seen.has(r.name.toLowerCase()))];
  }

  return dedupByName([...usdaResults, ...offResults]);
};
